import ManageProjectModal from '@/components/ManageProjectModal';
import { useCreateProject } from '@/services/project/hooks';
import { useProjects } from '@/services/project/hooks';
import { IconPlus } from '@/theme/icons';
import { Category } from '@/types/category';
import rem from '@/utils/rem';
import { Box, Button, Flex, Icon, Text, VStack, useDisclosure } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import ProjectDropdown from './ProjectDropdown';

interface ProjectsSectionProps {
  category: Category;
}

const ProjectsSection: React.FC<ProjectsSectionProps> = ({ category }) => {
  const { t } = useTranslation('CategorySpotlight');
  const { t: tCategoryDetails } = useTranslation('CategoryManagerDetails');
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  const { data: projects } = useProjects({
    refetchOnMount: true,
  });

  const { mutateAsync: createProject, isLoading: isCreatingProject } = useCreateProject();

  const {
    isOpen: isOpenCreateProjectModal,
    onOpen: openCreateProjectModal,
    onClose: onCloseCreateProjectModal,
  } = useDisclosure({ id: 'createNewProject' });

  // Filter projects by category
  const filteredProjects = useMemo(() => {
    return (
      projects?.project
        .filter((project) => project.categoryId === category?.id && !project.isArchived)
        .sort((a, b) => (b.order ?? 0) - (a.order ?? 0)) || []
    );
  }, [projects, category?.id]);

  // Set all projects as expanded by default when filtered projects change
  useEffect(() => {
    const projectIds = new Set(filteredProjects.map((project) => project.id));
    setExpandedProjects(projectIds);
  }, [filteredProjects]);

  const toggleProject = useCallback((projectId: string) => {
    setExpandedProjects((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  }, []);

  const onCreateProject = useCallback(
    async ({ name, purpose, result }: { name: string; result: string; purpose: string }) => {
      const id = uuidv4();

      if (!category?.id) return;

      await createProject({
        id,
        name,
        categoryId: category.id,
        result,
        purpose,
        imageUrl: category?.selectedImageUrl ?? '',
        isArchived: false,
      });

      onCloseCreateProjectModal();
    },
    [category?.id, category?.selectedImageUrl, createProject, onCloseCreateProjectModal],
  );

  return (
    <Flex flexDirection="column" minHeight="fit-content" padding={rem(24)} backgroundColor="background-primary">
      {/* Header */}
      <Flex alignItems="center" gap={rem(12)} marginBottom={rem(20)}>
        <Text as="h4" textStyle="h4BarSB" color="white" textTransform="uppercase">
          {t('projects.title')}
        </Text>
      </Flex>

      {/* Projects List */}
      <VStack alignItems="stretch" spacing={rem(16)}>
        {filteredProjects.length > 0 &&
          filteredProjects.map((project) => (
            <ProjectDropdown
              key={project.id}
              project={project}
              category={category}
              isExpanded={expandedProjects.has(project.id)}
              onToggle={toggleProject}
            />
          ))}

        <Box padding={rem(32)} textAlign="center" backgroundColor="background-tertiary">
          <Button
            height="auto"
            leftIcon={<Icon as={IconPlus} boxSize={rem(24)} />}
            onClick={openCreateProjectModal}
            paddingX={rem(32)}
            paddingY={rem(16)}
            variant="secondary"
          >
            <Text textStyle="large" color="text-primary">
              {tCategoryDetails('sections.magnificent7.buttons.createProject')}
            </Text>
          </Button>
        </Box>
      </VStack>

      {isOpenCreateProjectModal && (
        <ManageProjectModal
          isOpen={isOpenCreateProjectModal}
          isLoading={isCreatingProject}
          onSubmit={onCreateProject}
          onClose={onCloseCreateProjectModal}
          categoryId={category?.id}
          readonly
          readonlyTooltipLabel={tCategoryDetails('sections.magnificent7.tooltips.readonlyTooltipLabel')}
        />
      )}
    </Flex>
  );
};

export default ProjectsSection;
